// hooks/useEventSlotFormData.js
import { useState, useEffect, useMemo, useCallback } from "react";
import { EVENT_TYPES, getTodayISO, SLOT_STATUSES } from "../utils/index.js";
import { validateEventForm } from "../utils/validators.js";

/**
 * Hook pour gérer l'état du formulaire (avec participants locaux)
 */
export function useEventSlotFormData(event = null, slot = null) {
  const isEditing = !!(event && slot);

  const initialFormData = useMemo(
    () => ({
      name: "Entrainement",
      event_type: EVENT_TYPES.GROUPED_LESSON,
      slot_date: getTodayISO(),
      start_time: "19:00",
      end_time: "22:00",
      is_all_day: false,
      instructor_ids: [],
      participants: [], // { user_id, status }
      slot_status: SLOT_STATUSES.SCHEDULED,
    }),
    [],
  );

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [initialInstructorIds, setInitialInstructorIds] = useState([]);
  const [initialParticipants, setInitialParticipants] = useState([]); // [{ user_id, status }]

  // Initialiser le formulaire
  useEffect(() => {
    if (isEditing) {
      const instructorIds =
        slot.instructors?.map((inst) => inst.users?.id).filter(Boolean) || [];

      console.log(" Participants avec leurs statuts", slot.participants);
      // Participants avec leurs statuts
      const participants =
        slot.participants?.map((p) => ({
          user_id: p.users?.id || p.user_id, // Essayer d'abord users.id, sinon user_id
          status: p.status || "pending",
        })) || [];
      console.log(" Participants avec leurs statuts ?", participants);

      setFormData({
        name: event.name || "Entrainement",
        event_type: event.event_type || EVENT_TYPES.GROUPED_LESSON,
        slot_date: slot.slot_date || getTodayISO(),
        start_time: slot.start_time || "19:00",
        end_time: slot.end_time || "22:00",
        is_all_day: slot.is_all_day || false,
        instructor_ids: instructorIds,
        participants: participants,
        slot_status: slot.slot_status || SLOT_STATUSES.SCHEDULED,
      });

      setInitialInstructorIds(instructorIds);
      setInitialParticipants(participants);
    } else {
      setFormData(initialFormData);
      setInitialInstructorIds([]);
      setInitialParticipants([]);
    }
  }, [event, slot, isEditing, initialFormData]);

  // Gestion des champs
  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === "checkbox" ? checked : value;

    setFormData((prev) => {
      const updated = { ...prev, [name]: newValue };
      if (name === "is_all_day" && checked) {
        updated.start_time = "";
        updated.end_time = "";
      }
      return updated;
    });

    setErrors((prev) => ({ ...prev, [name]: "" }));
  }, []);

  // Toggle instructeur
  const toggleInstructor = useCallback((id) => {
    setFormData((prev) => {
      const isSelected = prev.instructor_ids.includes(id);
      const instructor_ids = isSelected
        ? prev.instructor_ids.filter((i) => i !== id)
        : [...prev.instructor_ids, id];
      return { ...prev, instructor_ids };
    });
  }, []);

  // Toggle participant
  const toggleParticipant = useCallback((userId) => {
    console.log("toggleParticipant", userId);
    setFormData((prev) => {
      const isSelected = prev.participants.some((p) => p.user_id === userId);
      const participants = isSelected
        ? prev.participants.filter((p) => p.user_id !== userId)
        : [...prev.participants, { user_id: userId, status: "pending" }];
      console.log("setFormData", prev, participants);
      return { ...prev, participants };
    });
  }, []);

  // Changer le statut d'un participant
  const updateParticipantStatus = useCallback((userId, status) => {
    setFormData((prev) => ({
      ...prev,
      participants: prev.participants.map((p) =>
        p.user_id === userId ? { ...p, status } : p,
      ),
    }));
  }, []);

  // Obtenir le statut d'un participant
  const getParticipantStatus = useCallback(
    (userId) => {
      const participant = formData.participants.find(
        (p) => p.user_id === userId,
      );
      return participant?.status || "pending";
    },
    [formData.participants],
  );

  // Vérifier si un participant est sélectionné
  const isParticipantSelected = useCallback(
    (userId) => {
      return formData.participants.some((p) => p.user_id === userId);
    },
    [formData.participants],
  );

  return {
    formData,
    errors,
    setErrors,
    isEditing,
    initialInstructorIds,
    initialParticipants,
    handleChange,
    toggleInstructor,
    toggleParticipant,
    updateParticipantStatus,
    getParticipantStatus,
    isParticipantSelected,
  };
}
